import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerActivityTypes, moneyActivityTypes } from 'constants/customer-account.constants';
import {
	getBeneficiaryPayments,
	getBeneficiaryPaymentsPagination,
	clearBeneficiaryPayments,
} from 'state/reducers/customer-accounts.reducers';
import { getCompanyBeneficiaryPayments } from 'services/customer-accounts.service';
import { pagesEnum } from 'constants/ui.constants';
import { PaymentStatus } from 'constants/payment.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

type IProps = {
	companyId: string;
	beneficiaryId: string;
};

const INITIAL_SORT_BY = 'createdAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

export const BeneficiaryPaymentsTable: React.FC<IProps> = ({ companyId, beneficiaryId }) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const payments = useAppSelector(getBeneficiaryPayments);
	const pagination = useAppSelector(getBeneficiaryPaymentsPagination);
	const sortFieldMap: Record<string, string> = {
		createdAt: 'created_at',
		statusHebrew: 'status',
		typeHebrew: 'type',
	};
	const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

	const fetchPayments = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			if (!companyId || !beneficiaryId) return;
			await dispatch(
				getCompanyBeneficiaryPayments(parseInt(companyId), parseInt(beneficiaryId), {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
				})
			);
		},
		[companyId, beneficiaryId, dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchPayments,
	});

	// Initial fetch
	useEffect(() => {
		if (!companyId || !beneficiaryId) return;
		runFetch(0);
	}, [companyId, beneficiaryId, runFetch]);

	// Cleanup on unmount or beneficiary change
	useEffect(() => {
		return () => {
			dispatch(clearBeneficiaryPayments());
		};
	}, [beneficiaryId, dispatch]);

	const handleSortChangeWithState = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder),
		[handleSortChange]
	);

	// Navigation logic for payment row click
	const handleRowClick = (row: any) => {
		const isAwaitingStatus = ![
			PaymentStatus.AwaitingAuthorization,
			PaymentStatus.AwaitingFunds,
			PaymentStatus.Completed,
			PaymentStatus.Approved,
			PaymentStatus.Rejected,
			PaymentStatus.Failed,
			PaymentStatus.Pending,
		].includes(row?.status);

		if (isAwaitingStatus) {
			navigate(`/${pagesEnum.COMPANY_FEATURE_APPROVE}/${pendingRequestsTypes.PAYMENTS}/${row.id}`);
		} else {
			navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${companyId}/${moneyActivityTypes.PAYMENT}/${row.id}`);
		}
	};

	return (
		<Stack spacing={2}>
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{pagination.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowAction={handleRowClick}
				defaultSortBy={INITIAL_SORT_BY}
				defaultOrderBy={INITIAL_SORT_ORDER}
				data={TableConfig.tableData(payments)}
				columns={TableConfig.buildColumns(customerActivityTypes.MONEY_ACTIVITIES, t)}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: pagination.total,
					hasMore: pagination.hasMore,
					currentPage,
					onPageChange: handlePageChange,
					onSortChange: handleSortChangeWithState,
					isLoading,
				}}
			/>
		</Stack>
	);
};

export default BeneficiaryPaymentsTable;
